import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Spinner } from "./hsUtils";
import { CertCard } from "./CertCard";
import { BellRinging, Certificate, Clock, MagnifyingGlass, ShieldCheck, UserCircle } from "@phosphor-icons/react";

const ago = (iso) => {
  if (!iso) return "";
  const d = Math.round((Date.now() - new Date(iso)) / 86400000);
  return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
};

function Counts({ p, size = "text-[11px]" }) {
  return (
    <span className={`inline-flex items-center gap-2 ${size}`}>
      <span className="text-emerald-600 font-medium" title="In date">{p.valid} ✓</span>
      <span className={`font-medium ${p.expiring ? "text-amber-600" : "text-muted-foreground"}`} title="Expiring soon">{p.expiring} ⏱</span>
      <span className={`font-medium ${p.expired ? "text-red-600" : "text-muted-foreground"}`} title="Expired">{p.expired} ✕</span>
    </span>
  );
}

/** Office view of every engineer's certificate wall with one-tap reminders (in-app alert + e-mail). */
export function TeamCertificateWall() {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => API.get("/hs/certificate-wall").then((r) => { setD(r.data); setSel((s) => s || r.data.people[0]?.id || ""); }).catch(() => setD({ people: [], summary: {} }));
  useEffect(() => { load(); }, []);
  if (!d) return <Spinner />;
  const people = d.people.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.trade || "").toLowerCase().includes(q.toLowerCase()));
  const person = d.people.find((p) => p.id === sel);
  const remind = async (uid, ids, label) => {
    setBusy(ids?.[0] || uid);
    try {
      const { data } = await API.post(`/hs/certificate-wall/${uid}/remind`, ids ? { qualification_ids: ids } : {});
      toast.success(data.message); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || `Couldn't send the reminder${label ? ` for ${label}` : ""}`); } finally { setBusy(""); }
  };
  if (!d.people.length) return <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="team-wall-empty">No in-house engineers yet — add your team under In-House Team → Team.</div>;
  const due = person ? person.certificates.filter((c) => c.status !== "valid") : [];
  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-4" data-testid="team-certificate-wall">
      <aside className="space-y-2">
        <div className="grid grid-cols-3 gap-1.5 text-center" data-testid="team-wall-summary">
          <div className="rounded-lg border border-border bg-card p-2"><p className="text-lg font-bold font-[Chivo] text-emerald-600" data-testid="team-wall-valid">{d.summary.valid}</p><p className="text-[10px] text-muted-foreground">In date</p></div>
          <div className="rounded-lg border border-border bg-card p-2"><p className="text-lg font-bold font-[Chivo] text-amber-600" data-testid="team-wall-expiring">{d.summary.expiring}</p><p className="text-[10px] text-muted-foreground">Expiring</p></div>
          <div className="rounded-lg border border-border bg-card p-2"><p className="text-lg font-bold font-[Chivo] text-red-600" data-testid="team-wall-expired">{d.summary.expired}</p><p className="text-[10px] text-muted-foreground">Expired</p></div>
        </div>
        <div className="relative"><MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-muted-foreground" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find an engineer…" className="h-9 w-full rounded-md border border-border bg-background pl-7 pr-2 text-sm" data-testid="team-wall-search" /></div>
        <ul className="rounded-lg border border-border divide-y divide-border overflow-hidden" data-testid="team-wall-people">
          {people.map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => setSel(p.id)} className={`w-full text-left px-3 py-2 transition-colors ${sel === p.id ? "bg-primary/10" : "hover:bg-muted/40"}`} data-testid={`team-wall-person-${p.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-sm truncate">{p.name}</p>
                  <Counts p={p} />
                </div>
                <p className="text-[11px] text-muted-foreground truncate">{p.trade || "No trade set"} · {p.total} on record{p.next_expiry ? ` · next: ${p.next_expiry.type_name} in ${p.next_expiry.days_left}d` : ""}</p>
              </button>
            </li>
          ))}
          {!people.length && <li className="px-3 py-4 text-xs text-muted-foreground text-center">No match</li>}
        </ul>
      </aside>
      <section className="space-y-3 min-w-0">
        {person && (
          <>
            <div className="rounded-lg border border-border bg-card p-4 flex flex-wrap items-center gap-3" data-testid="team-wall-header">
              <UserCircle size={36} className="text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-sm" data-testid="team-wall-name">{person.name}</h3>
                <p className="text-xs text-muted-foreground">{person.trade || "No trade set"}{person.email ? ` · ${person.email}` : " · no e-mail on file"} · <Counts p={person} size="text-xs" /></p>
                {person.last_reminded_at && <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1 mt-0.5" data-testid="team-wall-last-reminded"><Clock size={11} /> Last reminded {ago(person.last_reminded_at)}</p>}
              </div>
              {due.length > 0 && (
                <Button size="sm" onClick={() => remind(person.id)} disabled={!!busy} data-testid="team-wall-remind-all"><BellRinging size={14} className="mr-1" /> Remind about {due.length} item{due.length === 1 ? "" : "s"}</Button>
              )}
              {!due.length && person.total > 0 && <span className="inline-flex items-center gap-1 text-xs text-emerald-700" data-testid="team-wall-all-good"><ShieldCheck size={14} weight="fill" /> Everything in date</span>}
            </div>
            {person.certificates.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="team-wall-none"><Certificate size={28} className="mx-auto mb-2" /> Nothing on {person.name.split(" ")[0]}'s record yet — add cards and tickets from the matrix, or assign a course.</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-3" data-testid="team-wall-cards">
                {person.certificates.map((c) => (
                  <CertCard key={c.id} c={c} share={false} actions={
                    c.status !== "valid" ? (
                      <Button size="sm" variant="outline" className="h-8 text-xs bg-background" disabled={!!busy} onClick={() => remind(person.id, [c.id], c.type_name)} title={c.last_reminded_at ? `Last reminded ${ago(c.last_reminded_at)} (${c.reminder_count}×)` : "Send an in-app alert and e-mail"} data-testid={`team-wall-remind-${c.id}`}>
                        <BellRinging size={13} className="mr-1" /> {busy === c.id ? "Sending…" : c.last_reminded_at ? `Remind again · ${ago(c.last_reminded_at)}` : "Remind"}
                      </Button>
                    ) : null
                  } />
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">Automatic reminders go to the engineer and the office at {d.summary.warn_days}, 14, 7 and 0 days. One-tap reminders add an in-app alert and an e-mail naming each certificate and its expiry date.</p>
          </>
        )}
      </section>
    </div>
  );
}
